/**
 * Horario semanal (solo lunes a sábado) guardado en el CharField `schedule` del run.
 * Formato: __WEEKLY__1=09:00;2=14:00;4=18:00 (día 1=Lun … 6=Sáb)
 */

export const WEEKLY_SCHEDULE_PREFIX = "__WEEKLY__";

/** @type {{ id: number; label: string; short: string }[]} */
export const WEEKLY_DAYS_MON_SAT = [
	{ id: 1, label: "Lunes", short: "Lun" },
	{ id: 2, label: "Martes", short: "Mar" },
	{ id: 3, label: "Miércoles", short: "Mié" },
	{ id: 4, label: "Jueves", short: "Jue" },
	{ id: 5, label: "Viernes", short: "Vie" },
	{ id: 6, label: "Sábado", short: "Sáb" },
];

const SHORT_BY_ID = Object.fromEntries(
	WEEKLY_DAYS_MON_SAT.map((d) => [d.id, d.short]),
);

export function normalizeTimeValue(value) {
	if (value == null || value === "") return "";
	const s = String(value).trim();
	const m = s.match(/^(\d{1,2}):(\d{2})/);
	if (!m) return s;
	
	let h = parseInt(m[1], 10);
	let min = parseInt(m[2], 10);
	
	if (h < 8) {
		h = 8;
		min = 0;
	} else if (h >= 22) {
		h = 22;
		min = 0;
	}

	return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}
/** @returns {Record<number, {start: string, end: string}>} */
export function emptyWeeklyDayMap() {
	return {
		1: { start: "", end: "" },
		2: { start: "", end: "" },
		3: { start: "", end: "" },
		4: { start: "", end: "" },
		5: { start: "", end: "" },
		6: { start: "", end: "" },
	};
}

/**
 * @param {Record<number, string>} dayMap claves 1–6
 * @returns {string} cadena vacía si no hay ningún día con hora
 */
export function serializeWeeklySchedule(dayMap) {
	const parts = [];
	for (let d = 1; d <= 6; d++) {
		const obj = dayMap[d];
		if (!obj) continue;
		
		const start = normalizeTimeValue(obj.start);
		const end = normalizeTimeValue(obj.end);
		
		if (start || end) {
			if (start && end) parts.push(`${d}=${start}-${end}`);
			else if (start) parts.push(`${d}=${start}`); // Retrocompatibilidad
			else if (end) parts.push(`${d}=-${end}`);
		}
	}
	if (parts.length === 0) return "";
	return WEEKLY_SCHEDULE_PREFIX + parts.join(";");
}

export function parseWeeklySchedule(str) {
	if (str == null || typeof str !== "string") return null;
	const t = str.trim();
	if (!t.startsWith(WEEKLY_SCHEDULE_PREFIX)) return null;
	const body = t.slice(WEEKLY_SCHEDULE_PREFIX.length).trim();
	
	const out = emptyWeeklyDayMap();
	if (!body) return out;
	
	for (const part of body.split(";")) {
		const p = part.trim();
		if (!p) continue;
		const eq = p.indexOf("=");
		if (eq === -1) continue;
		const day = parseInt(p.slice(0, eq), 10);
		if (day >= 1 && day <= 6) {
			const timeStr = p.slice(eq + 1).trim();
			// Detectamos si tiene hora de fin (ej: 09:00-11:00)
			if (timeStr.includes("-")) {
				const [s, e] = timeStr.split("-");
				out[day] = {
					start: normalizeTimeValue(s),
					end: normalizeTimeValue(e)
				};
			} else {
				// Formato antiguo: solo hora de inicio
				out[day] = {
					start: normalizeTimeValue(timeStr),
					end: ""
				};
			}
		}
	}
	return out;
}

/**
 * Texto legible para estudiantes / listados (oculta el prefijo interno).
 * @param {string | null | undefined} schedule
 * @returns {string}
 */
export function formatWeeklyScheduleForDisplay(schedule) {
	if (schedule == null || typeof schedule !== "string") return "";
	const t = schedule.trim();
	if (!t.startsWith(WEEKLY_SCHEDULE_PREFIX)) return t;
	
	const parsed = parseWeeklySchedule(t);
	if (!parsed) return t.replace(WEEKLY_SCHEDULE_PREFIX, "").trim() || t;
	
	const parts = [];
	for (let d = 1; d <= 6; d++) {
		const obj = parsed[d];
		if (obj && (obj.start || obj.end)) {
			let timeText = "";
			if (obj.start && obj.end) timeText = `${obj.start} a ${obj.end}`;
			else if (obj.start) timeText = obj.start;
			else if (obj.end) timeText = `Hasta ${obj.end}`;
			
			parts.push(`${SHORT_BY_ID[d]} ${timeText}`);
		}
	}
	return parts.join("; ");
}
